<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue"
import { useRoute, useRouter } from "vue-router"

import Button from "@/components/shared/Button.vue"
import Card from "@/components/shared/Card.vue"
import CardActions from "@/components/shared/CardActions.vue"
import CardContent from "@/components/shared/CardContent.vue"
import CardTitle from "@/components/shared/CardTitle.vue"
import InputBox from "@/components/shared/InputBox.vue"
import InputSelect from "@/components/shared/InputSelect.vue"
import LayoutAuthed from "@/components/shared/LayoutAuthed.vue"
import StatusBadge from "@/components/shared/StatusBadge.vue"
import Table from "@/components/shared/Table.vue"
import TablePaginator from "@/components/shared/TablePaginator.vue"
import { getLocaleMessages } from "@/locales/index"
import stockInquiryService, { type ListRequest } from "@/services/stock-inquiry"
import type { StockCheckDetail } from "@/types/stock-check.type"
import { reportSentry } from "@/utils/sentry"

const route = useRoute()
const router = useRouter()

const inquiries = ref<StockCheckDetail[]>([])
const total = ref(0)

const query = ref<ListRequest>({
  page: (route.query.page as string) || "1",
  perPage: (route.query.perPage as string) || "25",
  customerCode: (route.query.customerCode as string) || "",
  status: (route.query.status as string) || "",
})

const statusOptions = computed(() => {
  const options = []
  const statusList = getLocaleMessages().stockInquiry.statusList
  for (const key in statusList) options.push({ value: key, label: statusList[key as keyof typeof statusList] })
  return options
})

// fetch function
async function loadInquiries() {
  try {
    const params = Object.entries(query.value).reduce((acc, [key, value]) => {
      acc[key as keyof ListRequest] = value === "" ? null : value
      return acc
    }, {} as ListRequest)

    const data = await stockInquiryService.list(params)
    inquiries.value = data.items
    total.value = data.total
  } catch (error) {
    console.error(error)
    reportSentry(error)
  }
}

// initial load
onMounted(async () => {
  await loadInquiries()
})

// re-load whenever the query string changes
watch(
  () => route.query,
  (newQuery) => {
    query.value = {
      page: (newQuery.page as string) || "1",
      perPage: (newQuery.perPage as string) || "25",
      customerCode: (newQuery.customerCode as string) || "",
      status: (newQuery.status as string) || "",
    }
    loadInquiries()
  },
)

// update URL (and trigger the watch) when the form submits
function searchInquiries() {
  query.value.page = "1"
  const params = Object.fromEntries(Object.entries(query.value).filter(([_, v]) => v !== ""))

  router.push({ path: route.path, query: params })
}
</script>

<template>
  <LayoutAuthed :title="$t('nav.stockInquiries')">
    <Card>
      <CardTitle>{{ $t("shared.search") }}</CardTitle>
      <form @submit.prevent="searchInquiries()">
        <CardContent>
          <div class="row">
            <div class="col-12 col-md-6 col-xl-3 mb-3">
              <InputBox
                v-model="query.customerCode"
                :label="$t('stockInquiry.customerCode')"
                type="text "
                field="customerCode"
              />
            </div>
            <div class="col-12 col-md-6 col-xl-3 mb-3">
              <InputSelect
                v-model="query.status"
                :label="$t('stockInquiry.status')"
                field="status"
                :options="statusOptions"
              />
            </div>
          </div>
        </CardContent>
        <CardActions>
          <Button type="submit" :text="$t('shared.search')" />
        </CardActions>
      </form>
    </Card>
    <Card>
      <CardTitle>{{ $t("shared.results") }} ({{ total }})</CardTitle>
      <CardContent>
        <Table>
          <template #header>
            <tr>
              <th>{{ $t("stockInquiry.code") }}</th>
              <th>{{ $t("stockInquiry.customerCode") }}</th>
              <th>{{ $t("stockInquiry.items") }}</th>
              <th>{{ $t("stockInquiry.status") }}</th>
            </tr>
          </template>
          <template #default>
            <tr
              v-for="(inquiry, index) in inquiries"
              :key="index"
              :class="{ 'new-inquiry': inquiry.status === 'requested' }"
            >
              <td>
                <router-link :to="`/stock_inquiries/${inquiry.id}`">{{ inquiry.code }}</router-link>
              </td>
              <td>{{ inquiry.customerCode }}</td>
              <td>{{ inquiry.stockCheckProducts.length }}</td>
              <td>
                <StatusBadge model="stockInquiry" field="status" :value="inquiry.status" />
              </td>
            </tr>
          </template>
        </Table>
        <TablePaginator
          :total="total"
          :perPage="Number(query.perPage)"
          :page="Number(query.page)"
          :url="route.fullPath"
        />
      </CardContent>
    </Card>
  </LayoutAuthed>
</template>

<style scoped>
tr.new-inquiry {
  td {
    background-color: var(--bs-danger-bg-subtle);
  }
}
</style>
